import React, { useState, useEffect, useCallback } from "react";
import { API } from "../../App";
import { toast } from "sonner";
import axios from "axios";
import {
  Bell, Mail, MessageSquare, Clock, AlertTriangle, CheckCircle,
  Settings, Play, RefreshCw, ChevronRight, DollarSign,
  ToggleLeft, ToggleRight, Edit, Save, X, Send, Calendar,
  Info, Zap
} from "lucide-react";

const FLAG_STYLES = {
  upcoming: { bg: "bg-blue-500/20", text: "text-blue-400", label: "Upcoming" },
  due_today: { bg: "bg-yellow-500/20", text: "text-yellow-400", label: "Due Today" },
  overdue: { bg: "bg-red-500/20", text: "text-red-400", label: "Overdue" },
};

const RemindersTab = ({ onOpenClient }) => {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [templateDraft, setTemplateDraft] = useState("");
  const [smsTemplateDraft, setSmsTemplateDraft] = useState("");
  const [view, setView] = useState("overview");

  const fetchAll = useCallback(async () => {
    try {
      const [settingsRes, statsRes, historyRes] = await Promise.all([
        axios.get(`${API}/reminder-settings`, { withCredentials: true }),
        axios.get(`${API}/reminders/stats`, { withCredentials: true }),
        axios.get(`${API}/reminders/history?limit=25`, { withCredentials: true }),
      ]);
      setSettings(settingsRes.data);
      setStats(statsRes.data);
      setHistory(historyRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleEnabled = async () => {
    try {
      await axios.patch(`${API}/reminder-settings`, { enabled: !settings.enabled }, { withCredentials: true });
      setSettings(p => ({ ...p, enabled: !p.enabled }));
      toast.success(settings.enabled ? "Reminders disabled" : "Reminders enabled");
    } catch (e) { toast.error("Failed to update"); }
  };

  const toggleTrigger = async (triggerId) => {
    const updated = settings.triggers.map(t =>
      t.id === triggerId ? { ...t, enabled: !t.enabled } : t
    );
    try {
      await axios.patch(`${API}/reminder-settings`, { triggers: updated }, { withCredentials: true });
      setSettings(p => ({ ...p, triggers: updated }));
      toast.success("Trigger updated");
    } catch (e) { toast.error("Failed to update trigger"); }
  };

  const updateTriggerDays = async (triggerId, newDays) => {
    const updated = settings.triggers.map(t =>
      t.id === triggerId ? { ...t, days_offset: parseInt(newDays) } : t
    );
    try {
      await axios.patch(`${API}/reminder-settings`, { triggers: updated }, { withCredentials: true });
      setSettings(p => ({ ...p, triggers: updated }));
      toast.success("Timing updated");
    } catch (e) { toast.error("Failed to update"); }
  };

  const saveTemplates = async () => {
    try {
      await axios.patch(`${API}/reminder-settings`, {
        email_template: templateDraft,
        sms_template: smsTemplateDraft
      }, { withCredentials: true });
      setSettings(p => ({ ...p, email_template: templateDraft, sms_template: smsTemplateDraft }));
      setEditingTemplate(false);
      toast.success("Templates saved");
    } catch (e) { toast.error("Failed to save"); }
  };

  const toggleEmailEnabled = async () => {
    try {
      await axios.patch(`${API}/reminder-settings`, { email_enabled: !settings.email_enabled }, { withCredentials: true });
      setSettings(p => ({ ...p, email_enabled: !p.email_enabled }));
      toast.success(settings.email_enabled ? "Email reminders disabled" : "Email reminders enabled");
    } catch (e) { toast.error("Failed to update"); }
  };

  const processReminders = async () => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/reminders/process`, {}, { withCredentials: true });
      const d = res.data;
      toast.success(`Processed ${d.processed} reminders, ${d.sent} sent`);
      fetchAll();
    } catch (e) { toast.error("Failed to process reminders"); }
    finally { setProcessing(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#FF6B35]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="reminders-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">Invoice Reminders</h2>
          <p className="text-[#A0AAB2] text-sm">Automated revenue recovery system</p>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={processReminders} disabled={processing}
            className="flex items-center space-x-2 px-4 py-2 bg-[#FF6B35]/20 text-[#FF6B35] rounded-lg text-sm font-medium hover:bg-[#FF6B35]/30 disabled:opacity-50"
            data-testid="process-reminders-btn">
            {processing ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
            <span>{processing ? "Processing..." : "Send Reminders Now"}</span>
          </button>
          <button onClick={toggleEnabled}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium ${
              settings?.enabled ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
            }`} data-testid="toggle-reminders-btn">
            {settings?.enabled ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
            <span>{settings?.enabled ? "Active" : "Disabled"}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Overdue" value={stats.overdue_count} sub={`$${stats.overdue_amount.toLocaleString(undefined, {minimumFractionDigits: 2})}`} color="text-red-400" icon={AlertTriangle} />
          <StatCard label="Due Today" value={stats.due_today_count} color="text-yellow-400" icon={Clock} />
          <StatCard label="Upcoming" value={stats.upcoming_count} color="text-blue-400" icon={Calendar} />
          <StatCard label="Outstanding" value={`$${stats.total_outstanding.toLocaleString(undefined, {minimumFractionDigits: 2})}`} color="text-[#FF6B35]" icon={DollarSign} />
          <StatCard label="Reminders Sent" value={stats.total_reminders_sent + stats.total_reminders_queued} sub={stats.email_configured ? "Email active" : "Email not configured"} color="text-green-400" icon={Send} />
        </div>
      )}

      {/* Email Config Warning */}
      {stats && !stats.email_configured && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-start space-x-3" data-testid="email-config-warning">
          <Info size={18} className="text-yellow-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-yellow-400 text-sm font-medium">Email Not Configured</p>
            <p className="text-[#A0AAB2] text-xs mt-1">Add your Resend API key to enable email delivery. Reminders will be logged but not sent until configured. Add <code className="bg-[#2A2B32] px-1.5 py-0.5 rounded text-[#FF6B35]">RESEND_API_KEY</code> to your environment.</p>
          </div>
        </div>
      )}

      {/* View Tabs */}
      <div className="flex space-x-1 bg-[#15161C] rounded-lg p-1 border border-[#2A2B32]">
        {[
          { id: "overview", label: "Invoices", icon: DollarSign },
          { id: "triggers", label: "Triggers", icon: Zap },
          { id: "templates", label: "Templates", icon: Mail },
          { id: "history", label: "History", icon: Clock },
        ].map(t => (
          <button key={t.id} onClick={() => setView(t.id)}
            data-testid={`reminder-view-${t.id}`}
            className={`flex-1 flex items-center justify-center space-x-2 py-2.5 rounded-md text-sm font-medium transition-all ${
              view === t.id ? "bg-[#FF6B35] text-white" : "text-[#A0AAB2] hover:text-white"
            }`}>
            <t.icon size={14} /><span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* View Content */}
      {view === "overview" && <InvoiceOverview stats={stats} onOpenClient={onOpenClient} />}
      {view === "triggers" && <TriggersView settings={settings} onToggle={toggleTrigger} onUpdateDays={updateTriggerDays} />}
      {view === "templates" && <TemplatesView
        settings={settings} editing={editingTemplate}
        emailDraft={templateDraft} smsDraft={smsTemplateDraft}
        onEditStart={() => { setEditingTemplate(true); setTemplateDraft(settings.email_template || ""); setSmsTemplateDraft(settings.sms_template || ""); }}
        onSave={saveTemplates} onCancel={() => setEditingTemplate(false)}
        setEmailDraft={setTemplateDraft} setSmsDraft={setSmsTemplateDraft}
        onToggleEmail={toggleEmailEnabled}
      />}
      {view === "history" && <HistoryView history={history} />}
    </div>
  );
};

const StatCard = ({ label, value, sub, color, icon: Icon }) => (
  <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-[#A0AAB2] text-xs">{label}</span>
      <Icon size={14} className={color} />
    </div>
    <p className={`text-xl font-bold ${color}`}>{value}</p>
    {sub && <p className="text-[#A0AAB2] text-[10px] mt-0.5">{sub}</p>}
  </div>
);

/* ===== Invoice Overview ===== */
const InvoiceOverview = ({ stats, onOpenClient }) => {
  if (!stats?.invoice_statuses?.length) {
    return (
      <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-8 text-center">
        <CheckCircle size={40} className="mx-auto text-green-400/30 mb-3" />
        <p className="text-[#A0AAB2]">No outstanding invoices</p>
        <p className="text-[#A0AAB2]/60 text-sm mt-1">All invoices are paid. Great job!</p>
      </div>
    );
  }

  const sorted = [...stats.invoice_statuses].sort((a, b) => {
    const order = { overdue: 0, due_today: 1, upcoming: 2 };
    return (order[a.flag] ?? 3) - (order[b.flag] ?? 3);
  });

  return (
    <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
      <h3 className="text-white font-semibold text-sm mb-3">Outstanding Invoices ({sorted.length})</h3>
      <div className="space-y-2">
        {sorted.map(inv => {
          const style = FLAG_STYLES[inv.flag] || FLAG_STYLES.upcoming;
          return (
            <div key={inv.id}
              onClick={() => onOpenClient && inv.client_id && onOpenClient(inv.client_id)}
              className="bg-[#1F2027] border border-[#2A2B32] rounded-lg p-3 flex items-center justify-between cursor-pointer hover:border-[#FF6B35]/50 transition-all"
              data-testid={`reminder-invoice-${inv.id}`}>
              <div className="flex items-center space-x-3">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${style.bg} ${style.text}`}>
                  {style.label}
                </span>
                <div>
                  <span className="text-white text-sm font-medium">{inv.invoice_number}</span>
                  <span className="text-[#A0AAB2] text-xs ml-2">{inv.client_name}</span>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-[#A0AAB2] text-xs">{inv.due_date}</span>
                <span className={`text-sm font-bold ${style.text}`}>
                  ${inv.balance.toLocaleString(undefined, {minimumFractionDigits: 2})}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ===== Triggers View ===== */
const TriggersView = ({ settings, onToggle, onUpdateDays }) => (
  <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6 space-y-4" data-testid="triggers-view">
    <h3 className="text-white font-semibold text-sm">Reminder Triggers</h3>
    <p className="text-[#A0AAB2] text-xs">Configure when reminders are sent relative to the invoice due date.</p>
    <div className="space-y-3">
      {settings?.triggers?.map(trigger => (
        <div key={trigger.id} className="bg-[#1F2027] border border-[#2A2B32] rounded-lg p-4 flex items-center justify-between" data-testid={`trigger-${trigger.id}`}>
          <div className="flex items-center space-x-3">
            <button onClick={() => onToggle(trigger.id)}
              className={trigger.enabled ? "text-green-400" : "text-[#A0AAB2]"}
              data-testid={`toggle-trigger-${trigger.id}`}>
              {trigger.enabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
            </button>
            <div>
              <p className="text-white text-sm font-medium">{trigger.label}</p>
              <p className="text-[#A0AAB2] text-xs">
                {trigger.days_offset < 0 ? `${Math.abs(trigger.days_offset)} days before due date` :
                 trigger.days_offset === 0 ? "On the due date" :
                 `${trigger.days_offset} days after due date`}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-[#A0AAB2] text-xs">Days offset:</span>
            <input type="number" value={trigger.days_offset}
              onChange={(e) => onUpdateDays(trigger.id, e.target.value)}
              className="w-16 bg-[#15161C] border border-[#2A2B32] rounded-lg px-2 py-1 text-white text-sm text-center"
              data-testid={`days-input-${trigger.id}`} />
          </div>
        </div>
      ))}
    </div>
    <div className="bg-[#1F2027] rounded-lg p-3 border border-[#2A2B32]">
      <p className="text-[#A0AAB2] text-xs">
        <strong className="text-white">How it works:</strong> Negative values = days before due date. Zero = on due date. Positive = days after due date (overdue).
        Click "Send Reminders Now" to process all matching invoices for today.
      </p>
    </div>
  </div>
);

/* ===== Templates View ===== */
const TemplatesView = ({ settings, editing, emailDraft, smsDraft, onEditStart, onSave, onCancel, setEmailDraft, setSmsDraft, onToggleEmail }) => (
  <div className="space-y-4" data-testid="templates-view">
    {/* Channel Controls */}
    <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
      <h3 className="text-white font-semibold text-sm mb-3">Communication Channels</h3>
      <div className="space-y-3">
        <div className="flex items-center justify-between bg-[#1F2027] rounded-lg p-3">
          <div className="flex items-center space-x-3">
            <Mail size={16} className="text-blue-400" />
            <div>
              <p className="text-white text-sm font-medium">Email</p>
              <p className="text-[#A0AAB2] text-xs">Send reminders via email</p>
            </div>
          </div>
          <button onClick={onToggleEmail}
            className={settings?.email_enabled ? "text-green-400" : "text-[#A0AAB2]"}
            data-testid="toggle-email-channel">
            {settings?.email_enabled ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
          </button>
        </div>
        <div className="flex items-center justify-between bg-[#1F2027] rounded-lg p-3 opacity-60">
          <div className="flex items-center space-x-3">
            <MessageSquare size={16} className="text-green-400" />
            <div>
              <p className="text-white text-sm font-medium">SMS (Coming Soon)</p>
              <p className="text-[#A0AAB2] text-xs">Twilio integration ready — API key required</p>
            </div>
          </div>
          <span className="text-[#A0AAB2] text-xs px-2 py-1 bg-[#2A2B32] rounded-lg">Future</span>
        </div>
      </div>
    </div>

    {/* Email Template */}
    <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-semibold text-sm">Email Template</h3>
        {!editing ? (
          <button onClick={onEditStart} className="flex items-center space-x-1 text-[#FF6B35] text-sm hover:underline" data-testid="edit-template-btn">
            <Edit size={12} /><span>Edit</span>
          </button>
        ) : (
          <div className="flex items-center space-x-2">
            <button onClick={onCancel} className="text-[#A0AAB2] text-sm hover:text-white"><X size={14} /></button>
            <button onClick={onSave} className="flex items-center space-x-1 text-green-400 text-sm hover:underline" data-testid="save-template-btn">
              <Save size={12} /><span>Save</span>
            </button>
          </div>
        )}
      </div>
      {editing ? (
        <textarea value={emailDraft} onChange={e => setEmailDraft(e.target.value)} rows={8}
          className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white text-sm font-mono resize-none"
          data-testid="email-template-input" />
      ) : (
        <pre className="bg-[#1F2027] rounded-lg p-4 text-[#A0AAB2] text-sm whitespace-pre-wrap font-mono">{settings?.email_template || "No template configured"}</pre>
      )}
      <div className="mt-3 bg-[#1F2027] rounded-lg p-3">
        <p className="text-[#A0AAB2] text-xs">
          <strong className="text-white">Variables:</strong>{" "}
          <code className="text-[#FF6B35]">{"{client_name}"}</code>,{" "}
          <code className="text-[#FF6B35]">{"{invoice_number}"}</code>,{" "}
          <code className="text-[#FF6B35]">{"{amount}"}</code>,{" "}
          <code className="text-[#FF6B35]">{"{status_message}"}</code>,{" "}
          <code className="text-[#FF6B35]">{"{payment_link}"}</code>
        </p>
      </div>
    </div>

    {/* SMS Template (read only, future) */}
    <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4 opacity-60">
      <h3 className="text-white font-semibold text-sm mb-3">SMS Template (Future)</h3>
      {editing ? (
        <textarea value={smsDraft} onChange={e => setSmsDraft(e.target.value)} rows={3}
          className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white text-sm font-mono resize-none" />
      ) : (
        <pre className="bg-[#1F2027] rounded-lg p-4 text-[#A0AAB2] text-sm whitespace-pre-wrap font-mono">{settings?.sms_template || "No template configured"}</pre>
      )}
    </div>
  </div>
);

/* ===== History View ===== */
const HistoryView = ({ history }) => (
  <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4" data-testid="history-view">
    <h3 className="text-white font-semibold text-sm mb-3">Reminder History</h3>
    {history.length === 0 ? (
      <div className="text-center py-8">
        <Clock size={40} className="mx-auto text-[#2A2B32] mb-3" />
        <p className="text-[#A0AAB2]">No reminders sent yet</p>
        <p className="text-[#A0AAB2]/60 text-sm mt-1">Reminders will appear here after processing.</p>
      </div>
    ) : (
      <div className="space-y-2">
        {history.map(log => (
          <div key={log.id} className="bg-[#1F2027] border border-[#2A2B32] rounded-lg p-3" data-testid={`reminder-log-${log.id}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-2">
                <span className={`w-2 h-2 rounded-full ${
                  log.email_status === "sent" ? "bg-green-400" :
                  log.email_status === "queued" ? "bg-yellow-400" :
                  log.email_status === "failed" ? "bg-red-400" : "bg-gray-400"
                }`}></span>
                <span className="text-white text-sm font-medium">{log.client_name}</span>
                <span className="text-[#A0AAB2] text-xs">{log.invoice_number}</span>
              </div>
              <span className="text-[#A0AAB2] text-[10px]">{new Date(log.created_at).toLocaleString()}</span>
            </div>
            <div className="flex items-center space-x-3 text-xs text-[#A0AAB2]">
              <span className="flex items-center space-x-1">
                <Bell size={10} /><span>{log.trigger_label}</span>
              </span>
              <span className="flex items-center space-x-1">
                <DollarSign size={10} /><span>${log.amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
              </span>
              <span className="flex items-center space-x-1">
                <Mail size={10} />
                <span className={
                  log.email_status === "sent" ? "text-green-400" :
                  log.email_status === "queued" ? "text-yellow-400" :
                  log.email_status === "failed" ? "text-red-400" : ""
                }>{log.email_status}</span>
              </span>
              <span className="flex items-center space-x-1">
                <MessageSquare size={10} /><span>{log.sms_status}</span>
              </span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default RemindersTab;
